import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { api, loadUser, clearAuth } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import VSLogo from "@/components/VSLogo";
import {
  Home, Briefcase, Bookmark, BellRing, Sparkles, Rocket, ClipboardList, GitBranch,
  Award, LineChart, Building2, Globe, GraduationCap, Newspaper, BookOpen, Users, Bot,
  UserCircle, LogOut, ExternalLink, Plus, Trash2, Search, ChevronRight, Trophy, CalendarDays,
} from "lucide-react";

const NAV = [
  { group: "MY CAREER", items: [
    { key: "home", to: "/candidate/hub", label: "Career Home", icon: Home },
    { key: "apps", to: "/candidate/hub/applications", label: "My Applications", icon: Briefcase },
    { key: "saved", to: "/candidate/hub/saved-jobs", label: "Saved Jobs", icon: Bookmark },
    { key: "alerts", to: "/candidate/hub/job-alerts", label: "Job Alerts", icon: BellRing },
    { key: "recos", to: "/candidate/hub/recommended", label: "Recommended", icon: Sparkles },
  ]},
  { group: "BUILD EXPERIENCE", items: [
    { key: "exp", to: "/candidate/hub/experience", label: "Experience Hub", icon: Rocket },
    { key: "practice", to: "/candidate/hub/practice", label: "Practice Challenges", icon: ClipboardList },
    { key: "hack", to: "/candidate/hub/hackathons", label: "Hackathons", icon: Trophy },
    { key: "events", to: "/candidate/hub/events", label: "Career Events", icon: CalendarDays },
  ]},
  { group: "DEVELOP SKILLS", items: [
    { key: "roadmap", to: "/candidate/hub/roadmap", label: "Career Roadmap", icon: GitBranch },
    { key: "assessments", to: "/candidate/hub/assessments", label: "My Assessments", icon: LineChart },
    { key: "interviews", to: "/candidate/hub/interviews", label: "My Interviews", icon: Award },
  ]},
  { group: "PROFESSIONAL", items: [
    { key: "profile", to: "/candidate/profile", label: "My Profile", icon: UserCircle },
    { key: "projects", to: "/candidate/hub/projects", label: "Projects", icon: GitBranch },
    { key: "certs", to: "/candidate/hub/certifications", label: "Certifications", icon: Award },
    { key: "journey", to: "/candidate/hub/journey", label: "Career Journey", icon: LineChart },
  ]},
  { group: "OPPORTUNITIES", items: [
    { key: "companies", to: "/candidate/hub/companies", label: "Company Directory", icon: Building2 },
    { key: "external", to: "/candidate/hub/external", label: "External Opportunities", icon: Globe },
  ]},
  { group: "COMMUNITY", items: [
    { key: "updates", to: "/candidate/hub/updates", label: "Career Updates", icon: Newspaper },
    { key: "resources", to: "/candidate/hub/resources", label: "Career Resources", icon: BookOpen },
    { key: "coach", to: "/candidate/hub/coach", label: "VS AI Career Coach", icon: Bot },
  ]},
];

export default function CandidateHubLayout({ children }) {
  const user = loadUser();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const logout = () => { clearAuth(); nav("/login"); };
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 w-60 bg-[#0A2540] hidden md:flex flex-col text-slate-100">
        <div className="p-5 border-b border-white/10 flex items-center gap-2">
          <VSLogo dark size={28} />
          <div>
            <div className="text-sm font-semibold">VS Career Hub</div>
            <div className="text-[10px] uppercase tracking-widest text-[#F59E0B]">Candidate Portal</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {NAV.map(group => (
            <div key={group.group}>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{group.group}</div>
              <div className="mt-1 space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, key }) => {
                  const active = pathname === to || (to !== "/candidate/hub" && pathname.startsWith(to));
                  return (
                    <Link key={key} to={to} data-testid={`hub-nav-${key}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        active ? "bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30"
                                : "text-slate-300 hover:text-white hover:bg-white/5"
                      }`}>
                      <Icon className="w-4 h-4" /><span>{label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400">Logged in as</div>
          <div className="text-sm font-medium truncate">{user?.full_name}</div>
          <Button onClick={logout} variant="ghost" size="sm" className="mt-2 w-full text-slate-300 hover:bg-white/5" data-testid="hub-logout">
            <LogOut className="w-4 h-4 mr-1.5" />Logout
          </Button>
        </div>
      </aside>
      <main className="md:pl-60 min-h-screen">
        <div className="p-6 md:p-10">{children}</div>
      </main>
    </div>
  );
}

// ─── Career Home ───────────────────────────────────────────────────────
export function CareerHome() {
  const user = loadUser();
  const [snap, setSnap] = useState({});
  const [next, setNext] = useState(null);
  const [recos, setRecos] = useState([]);
  useEffect(() => {
    api.get("/candidate/hub-summary").then(r => setSnap(r.data?.snapshot || {}));
    api.get("/candidate/profile-actions").then(r => setNext(r.data?.next_action || null)).catch(() => {});
    api.get("/candidate/recommendations").then(r => setRecos(r.data?.jobs || r.data || [])).catch(() => {});
  }, []);
  const KPI = ({ label, value, testid, to }) => (
    <Link to={to} data-testid={testid}>
      <Card className="p-5 hover:shadow-md transition-shadow border-slate-200">
        <div className="text-3xl font-bold text-[#0A2540]">{value ?? 0}</div>
        <div className="text-sm text-slate-500 mt-1">{label}</div>
      </Card>
    </Link>
  );
  return (
    <CandidateHubLayout>
      <div className="max-w-6xl">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0A2540]" data-testid="hub-welcome">
          Welcome back, {user?.full_name}
        </h1>
        <p className="text-slate-500 mt-1">Your career snapshot at VS-Solutions</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <KPI label="Applications" value={snap.applications} to="/candidate/hub/applications" testid="kpi-apps" />
          <KPI label="Saved Jobs" value={snap.saved_jobs} to="/candidate/hub/saved-jobs" testid="kpi-saved" />
          <KPI label="Assessments Pending" value={snap.assessments_pending} to="/candidate/hub/assessments" testid="kpi-asmts" />
          <KPI label="Upcoming Interviews" value={snap.upcoming_interviews} to="/candidate/hub/interviews" testid="kpi-int" />
          <KPI label="Offers" value={snap.offers} to="/candidate/hub/applications" testid="kpi-offers" />
          <KPI label="Projects" value={snap.projects} to="/candidate/hub/projects" testid="kpi-projects" />
          <KPI label="Hackathons" value={snap.hackathon_registrations} to="/candidate/hub/hackathons" testid="kpi-hack" />
          <KPI label="Career Events" value={snap.event_registrations} to="/candidate/hub/events" testid="kpi-events" />
        </div>

        {next && (
          <Card className="mt-8 p-6 border-l-4 border-l-[#F59E0B]" data-testid="hub-next-action">
            <div className="text-xs uppercase tracking-widest text-[#F59E0B]">Your Next Action</div>
            <div className="mt-2 font-display text-xl text-[#0A2540]">{next.title || "Explore new opportunities"}</div>
            <p className="text-slate-600 mt-1">{next.body || "Check out new roles matching your profile."}</p>
            <Link to={next.cta_url || "/jobs"}>
              <Button className="mt-3 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-slate-900">{next.cta_text || "Explore Jobs"} <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </Card>
        )}

        {recos.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-xl text-[#0A2540] font-semibold">Recommended for you</h2>
            <p className="text-xs text-slate-400 mt-0.5">Match percentage is informational — not a hiring probability.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {recos.slice(0, 6).map(j => (
                <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`hub-reco-${j.id}`}>
                  <Card className="p-5 border-slate-200 hover:shadow-md transition-shadow">
                    <Badge variant="secondary" className="text-xs">{j.domain || j.department}</Badge>
                    <h3 className="font-display font-semibold text-[#0A2540] mt-2">{j.title}</h3>
                    <div className="text-sm text-slate-500 mt-1">{j.location} · {j.experience_min}–{j.experience_max} yrs</div>
                    {typeof j.match_pct === "number" && (
                      <div className="mt-3 text-xs text-[#F59E0B]">{j.match_pct}% match (informational)</div>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </CandidateHubLayout>
  );
}

// ─── Generic list page (data-driven, reusable) ─────────────────────────
export function HubListPage({ title, subtitle, endpoint, mapItem, testid, empty, filters }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const fetchIt = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(filter);
    api.get(`${endpoint}${p.toString() ? "?" + p.toString() : ""}`)
      .then(r => setItems(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [endpoint, filter]);
  useEffect(() => { fetchIt(); }, [fetchIt]);
  return (
    <CandidateHubLayout>
      <div className="max-w-6xl">
        <h1 className="font-display text-3xl font-bold text-[#0A2540]" data-testid={`${testid}-title`}>{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        {filters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {filters.map(f => (
              <Select key={f.key} value={filter[f.key] || ""} onValueChange={v => setFilter(cur => ({ ...cur, [f.key]: v || undefined }))}>
                <SelectTrigger className="w-40" data-testid={`${testid}-f-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                <SelectContent>
                  {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            {Object.keys(filter).length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setFilter({})}>Clear</Button>
            )}
          </div>
        )}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-list`}>
          {items.map(it => mapItem(it))}
        </div>
        {!loading && items.length === 0 && (
          <div className="text-center py-16 text-slate-500" data-testid={`${testid}-empty`}>
            {empty || "Nothing to show yet."}
          </div>
        )}
      </div>
    </CandidateHubLayout>
  );
}

// ─── Concrete list pages ──────────────────────────────────────────────
export function CompanyDirectory() {
  return <HubListPage
    title="Company Directory" subtitle="Explore companies. VS-Solutions never ranks or scores third-party companies."
    testid="hub-companies" endpoint="/hub/companies"
    empty="Company profiles will appear here as management adds them."
    mapItem={c => (
      <Card key={c.id} className="p-5 border-slate-200" data-testid={`hub-company-${c.id}`}>
        <div className="flex items-center gap-3">
          {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-10 h-10 rounded object-contain bg-white p-1 border" /> : <Building2 className="w-8 h-8 text-slate-400" />}
          <div>
            <h3 className="font-display font-semibold text-[#0A2540]">{c.name}</h3>
            <div className="text-xs text-slate-500">{c.industry || "—"}</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(c.career_domains || []).slice(0, 3).map(d => <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>)}
        </div>
        {c.careers_url && (
          <a href={c.careers_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center text-sm text-[#F59E0B]">
            Visit Official Careers Website <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </a>
        )}
      </Card>
    )} />;
}

export function ExternalOpportunities() {
  return <HubListPage
    title="External Opportunities" subtitle="Public career opportunities aggregated from external sources. Always labelled as external."
    testid="hub-external" endpoint="/hub/external-opportunities"
    filters={[{ key: "opportunity_type", label: "Type", options: ["Job", "Internship", "Graduate Program", "Apprenticeship", "Hackathon", "Challenge", "Learning Program", "Virtual Experience"] }]}
    empty="No external opportunities are currently listed. Management is verifying new ones — check back soon."
    mapItem={o => (
      <Card key={o.id} className="p-5 border-slate-200" data-testid={`hub-external-${o.id}`}>
        <div className="flex items-center justify-between">
          <Badge className="bg-[#0A2540] text-white hover:bg-[#0A2540]">External Opportunity</Badge>
          <Badge variant="secondary" className="text-xs">{o.opportunity_type}</Badge>
        </div>
        <h3 className="font-display font-semibold text-[#0A2540] mt-3">{o.title}</h3>
        <div className="text-xs text-slate-500 mt-1">{o.company_name || "—"} · {o.location || "—"}</div>
        {o.official_url && (
          <a href={o.official_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center text-sm text-[#F59E0B]">
            View on Official Site <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </a>
        )}
      </Card>
    )} />;
}

export function ExperienceHub() {
  return <HubListPage
    title="Experience Hub" subtitle="Build practical experience with curated projects across IT and Non-IT domains."
    testid="hub-experience" endpoint="/hub/experience-projects"
    filters={[{ key: "difficulty", label: "Difficulty", options: ["Beginner", "Intermediate", "Advanced"] }]}
    empty="Curated experience projects will appear here."
    mapItem={p => (
      <Card key={p.id} className="p-5 border-slate-200" data-testid={`hub-exp-${p.id}`}>
        <Badge variant="secondary" className="text-xs">{p.domain}</Badge>
        <h3 className="font-display font-semibold text-[#0A2540] mt-2">{p.title}</h3>
        <div className="text-xs text-slate-500 mt-1">{p.difficulty} · {p.estimated_duration || "Self-paced"}</div>
        <p className="text-sm text-slate-600 mt-3 line-clamp-3">{p.description}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.skills || []).slice(0, 4).map(s => <Badge key={s} className="bg-blue-50 text-blue-700 border-none text-xs">{s}</Badge>)}
        </div>
      </Card>
    )} />;
}

export function CareerResources() {
  return <HubListPage
    title="Career Resources" subtitle="Curated learning resources across coding, interview, aptitude and business skills. External resources labelled clearly."
    testid="hub-resources" endpoint="/hub/career-resources"
    filters={[{ key: "category", label: "Category", options: ["Coding", "Data", "AI-ML", "Cloud", "Cyber Security", "DevOps", "QA", "Resume", "Interview", "Communication", "Aptitude", "HR", "Business", "Leadership"] }]}
    empty="Career resources will appear here."
    mapItem={r => (
      <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`hub-resource-${r.id}`}>
        <Card className="p-5 border-slate-200 h-full hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <Badge variant="secondary" className="text-xs">{r.category}</Badge>
            {r.is_external && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-xs">External</Badge>}
          </div>
          <h3 className="font-display font-semibold text-[#0A2540] mt-3">{r.title}</h3>
          <div className="text-xs text-slate-500 mt-1">{r.source}</div>
          <div className="mt-3 text-sm text-[#F59E0B] inline-flex items-center">Open resource <ExternalLink className="w-3.5 h-3.5 ml-1" /></div>
        </Card>
      </a>
    )} />;
}

export function PracticeChallenges() {
  return <HubListPage
    title="Practice Challenges" subtitle="Practice performance is informational only and does NOT affect real recruitment assessments."
    testid="hub-practice" endpoint="/hub/practice-challenges"
    filters={[{ key: "difficulty", label: "Difficulty", options: ["Beginner", "Intermediate", "Advanced"] }]}
    empty="Practice challenges will appear here."
    mapItem={c => (
      <Card key={c.id} className="p-5 border-slate-200" data-testid={`hub-practice-${c.id}`}>
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="text-xs">{c.category}</Badge>
          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-xs">{c.difficulty}</Badge>
        </div>
        <h3 className="font-display font-semibold text-[#0A2540] mt-2">{c.title}</h3>
        <p className="text-sm text-slate-600 mt-2 line-clamp-3">{c.description}</p>
        <div className="text-xs text-slate-400 mt-3">{c.question_type}</div>
      </Card>
    )} />;
}

// ─── Projects portfolio (candidate-scoped CRUD) ────────────────────────
export function MyProjects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", technologies: "", github_url: "", demo_url: "" });
  const load = () => api.get("/candidate/projects").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name required");
    await api.post("/candidate/projects", {
      name: form.name, description: form.description,
      technologies: form.technologies.split(",").map(s => s.trim()).filter(Boolean),
      github_url: form.github_url || null, demo_url: form.demo_url || null,
      team_or_individual: "Individual",
    });
    setOpen(false); setForm({ name: "", description: "", technologies: "", github_url: "", demo_url: "" });
    load(); toast.success("Project added");
  };
  const del = async (id) => { await api.delete(`/candidate/projects/${id}`); load(); toast.success("Deleted"); };
  return (
    <CandidateHubLayout>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">Projects Portfolio</h1>
          <Button onClick={() => setOpen(true)} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-slate-900" data-testid="hub-project-add">
            <Plus className="w-4 h-4 mr-1" />Add Project
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {items.map(p => (
            <Card key={p.id} className="p-5 border-slate-200" data-testid={`hub-project-${p.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display font-semibold text-[#0A2540]">{p.name}</h3>
                <Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
              </div>
              <p className="text-sm text-slate-600 mt-2">{p.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.technologies || []).slice(0, 6).map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
              </div>
              <div className="mt-3 flex gap-3 text-sm">
                {p.github_url && <a href={p.github_url} target="_blank" rel="noreferrer" className="text-[#F59E0B]">GitHub</a>}
                {p.demo_url && <a href={p.demo_url} target="_blank" rel="noreferrer" className="text-[#F59E0B]">Live Demo</a>}
              </div>
            </Card>
          ))}
          {items.length === 0 && <div className="col-span-2 text-center py-12 text-slate-500">No projects yet. Add your first project to showcase your work.</div>}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-slate-900" data-testid="hub-project-dialog">
          <DialogHeader><DialogTitle>Add a project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Project name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="hub-project-name" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="hub-project-desc" />
            <Input placeholder="Technologies (comma-separated)" value={form.technologies} onChange={e => setForm({ ...form, technologies: e.target.value })} data-testid="hub-project-tech" />
            <Input placeholder="GitHub URL (optional)" value={form.github_url} onChange={e => setForm({ ...form, github_url: e.target.value })} />
            <Input placeholder="Live demo URL (optional)" value={form.demo_url} onChange={e => setForm({ ...form, demo_url: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-slate-900" data-testid="hub-project-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CandidateHubLayout>
  );
}

// ─── Certifications ────────────────────────────────────────────────────
export function MyCertifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", issuer: "", credential_id: "", credential_url: "", issue_date: "" });
  const load = () => api.get("/candidate/certifications").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Certification name required");
    await api.post("/candidate/certifications", form);
    setOpen(false); setForm({ name: "", issuer: "", credential_id: "", credential_url: "", issue_date: "" });
    load(); toast.success("Certification recorded");
  };
  const del = async (id) => { await api.delete(`/candidate/certifications/${id}`); load(); toast.success("Deleted"); };
  return (
    <CandidateHubLayout>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">Certifications & Achievements</h1>
          <Button onClick={() => setOpen(true)} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-slate-900" data-testid="hub-cert-add">
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {items.map(c => (
            <Card key={c.id} className="p-5 border-slate-200" data-testid={`hub-cert-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <Badge variant="secondary" className="text-xs">{c.category}</Badge>
                  <h3 className="font-display font-semibold text-[#0A2540] mt-2">{c.name}</h3>
                  <div className="text-sm text-slate-500 mt-1">{c.issuer || "—"}{c.issue_date ? ` · Issued ${c.issue_date}` : ""}</div>
                  {c.credential_url && <a href={c.credential_url} target="_blank" rel="noreferrer" className="text-sm text-[#F59E0B] mt-1 inline-block">View credential</a>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
              </div>
            </Card>
          ))}
          {items.length === 0 && <div className="col-span-2 text-center py-12 text-slate-500">Record your certifications, awards and workshop achievements here.</div>}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-slate-900">
          <DialogHeader><DialogTitle>Add certification / achievement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name (e.g. AWS Certified Cloud Practitioner)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="hub-cert-name" />
            <Input placeholder="Issuer" value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })} />
            <Input placeholder="Credential ID (optional)" value={form.credential_id} onChange={e => setForm({ ...form, credential_id: e.target.value })} />
            <Input placeholder="Credential URL (optional)" value={form.credential_url} onChange={e => setForm({ ...form, credential_url: e.target.value })} />
            <Input placeholder="Issue date (YYYY-MM-DD, optional)" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-slate-900" data-testid="hub-cert-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CandidateHubLayout>
  );
}

// ─── Career Journey ────────────────────────────────────────────────────
export function CareerJourney() {
  const [data, setData] = useState({ counts: {}, timeline: [] });
  useEffect(() => { api.get("/candidate/experience-timeline").then(r => setData(r.data || {})); }, []);
  return (
    <CandidateHubLayout>
      <div className="max-w-4xl">
        <h1 className="font-display text-3xl font-bold text-[#0A2540]">My Career Journey</h1>
        <p className="text-slate-500 mt-1">A dynamic timeline of everything you've done at VS-Solutions.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {Object.entries(data.counts || {}).map(([k, v]) => (
            <Card key={k} className="p-4 border-slate-200" data-testid={`hub-journey-count-${k}`}>
              <div className="text-2xl font-bold text-[#0A2540]">{v}</div>
              <div className="text-xs text-slate-500 mt-1 capitalize">{k.replace(/_/g, " ")}</div>
            </Card>
          ))}
        </div>
        <div className="mt-8 relative border-l-2 border-slate-200 pl-6 space-y-4">
          {(data.timeline || []).map((e, i) => (
            <div key={i} className="relative" data-testid={`hub-journey-event-${i}`}>
              <div className="absolute -left-[31px] w-4 h-4 rounded-full bg-[#F59E0B] border-4 border-white" />
              <div className="text-xs text-slate-400">{e.at}</div>
              <div className="text-sm text-[#0A2540] font-medium">{e.title}</div>
            </div>
          ))}
          {(!data.timeline || data.timeline.length === 0) && (
            <div className="text-slate-500 text-sm">Your career journey milestones will appear here as you engage with the platform.</div>
          )}
        </div>
      </div>
    </CandidateHubLayout>
  );
}

// ─── Career Roadmap picker ─────────────────────────────────────────────
export function CareerRoadmap() {
  const [maps, setMaps] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/hub/career-roadmaps").then(r => setMaps(r.data || [])); }, []);
  return (
    <CandidateHubLayout>
      <div className="max-w-5xl">
        <h1 className="font-display text-3xl font-bold text-[#0A2540]">Career Roadmap</h1>
        <p className="text-slate-500 mt-1">Structured learning paths across IT and Non-IT careers.</p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {maps.map(m => (
            <Card key={m.id} onClick={() => setSelected(m)} data-testid={`hub-roadmap-${m.id}`}
              className={`p-5 cursor-pointer border-slate-200 ${selected?.id === m.id ? "ring-2 ring-[#F59E0B]" : ""}`}>
              <Badge variant="secondary" className="text-xs">{m.domain}</Badge>
              <h3 className="font-display font-semibold text-[#0A2540] mt-2">{m.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-3">{m.description}</p>
            </Card>
          ))}
        </div>
        {selected && (
          <div className="mt-8">
            <h2 className="font-display text-xl text-[#0A2540] font-semibold">{selected.title}</h2>
            <div className="mt-4 space-y-4">
              {(selected.steps || []).map((s, i) => (
                <Card key={i} className="p-5 border-slate-200" data-testid={`hub-roadmap-step-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#F59E0B] text-slate-900 flex items-center justify-center font-bold text-sm">{i + 1}</div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase">{s.stage}</div>
                      <div className="font-display font-semibold text-[#0A2540]">{s.title}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(s.skills || []).map(sk => <Badge key={sk} className="bg-blue-50 text-blue-700 border-none text-xs">{sk}</Badge>)}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </CandidateHubLayout>
  );
}
